import { TFunction } from "i18next";
import React from "react";

const PDF_MAX_FILE_SIZE = 20000;
const IMAGE_MAX_FILE_SIZE = 15000;
const VIDEO_MAX_FILE_SIZE = 100000;
const AUDIO_MAX_FILE_SIZE = 50000;
const KILOBYTE_SIZE = 1024;
export const PDF_TYPE = "application/pdf";
export const IMAGE_TYPE = "image/*";
export const VIDEO_TYPE = "video/*";
export const AUDIO_TYPE =
  "video/*,audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/x-wav,audio/aac,audio/midi";

export const getUploadI18nNamespace = (type?: string, overrideNamespace?: string) => {
  if (overrideNamespace) return overrideNamespace;
  if (type === "video") return "video";
  if (type === "pdf") return "pdf";
  if (type === "audio") return "mp3";
  return "image";
};

export const fileExceedSize = (file: any) => {
  const fileSizeKB = Number((file.size / KILOBYTE_SIZE).toFixed(2));
  const isVideoExtension = /\.(mp4|h264|mov|mkv|hevc|ogg|webm)$/i.test(file.name);
  const isAudioExtension = /\.(mp3|ogg|wav)$/i.test(file.name);
  const isPdfExtension = /\.pdf$/i.test(file.name);

  const isPdf = file.type === "application/pdf" || isPdfExtension;
  const isImage = file.type.includes("image");
  const isVideo = file.type.includes("video") || isVideoExtension;
  const isAudio = file.type.includes("audio") || isAudioExtension;

  if (
    (isPdf && fileSizeKB > PDF_MAX_FILE_SIZE) ||
    (isImage && fileSizeKB > IMAGE_MAX_FILE_SIZE) ||
    (isVideo && fileSizeKB > VIDEO_MAX_FILE_SIZE) ||
    (isAudio && fileSizeKB > AUDIO_MAX_FILE_SIZE)
  ) {
    return {
      errorName: "maxSizeExceeded",
      errorMessage: isPdf
        ? "pdf_max_size_exceeded"
        : isImage
          ? "image_max_size_exceeded"
          : isAudio
            ? "audio_max_size_exceeded"
            : "video_max_size_exceeded",
    };
  }
  return false;
};

const getInvalidFileTypeErrorMessage = (uploadedFileType: string) => {
  return uploadedFileType === "pdf"
    ? "pdf_invalid_format"
    : uploadedFileType === "image"
      ? "image_invalid_format"
      : uploadedFileType === "video"
        ? "video_invalid_format"
        : "audio_invalid_format";
};

export const invalidFileType = (uploadedFileType: string, file: File) => {
  const isVideoExtension = /\.(mp4|h264|mov|mkv|hevc|ogg|webm)$/i.test(file.name);
  const isAudioExtension = /\.(mp3|ogg|wav)$/i.test(file.name);

  if (
    (uploadedFileType === "pdf" &&
      file.type !== PDF_TYPE &&
      !file.name.toLowerCase().endsWith(".pdf")) ||
    (uploadedFileType === "image" && !file.type.includes("image/")) ||
    (uploadedFileType === "video" && !file.type.includes("video/") && !isVideoExtension) ||
    (uploadedFileType === "audio" && !file.type.includes("audio/") && !isAudioExtension)
  ) {
    return {
      errorName: "invalidFileFormat",
      errorMessage: getInvalidFileTypeErrorMessage(uploadedFileType),
    };
  }

  return false;
};

export const getFileSizeInBytes = (file: string) => {
  const fileBlob = new Blob([file]);

  const fileSizeBytes = fileBlob.size;

  return fileSizeBytes;
};

export const fileRequireToBeChunked = (file: string, maxSize: number) => {
  const fileSizeBytes = getFileSizeInBytes(file);

  const fileSizeKB = Number((fileSizeBytes / KILOBYTE_SIZE).toFixed(2));

  return fileSizeKB >= maxSize;
};

export const base64ToBlob = async (base64Data: string) => {
  const data = await fetch(base64Data);
  const blob = data.blob();

  return blob;
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);

  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      resolve(reader.result as string);
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export const svgToBase64 = (svgString: string, canvasSize: number): Promise<string> => {
  const svgBuffer = new TextEncoder().encode(svgString);
  const base64SVG = btoa(String.fromCharCode(...new Uint8Array(svgBuffer)));

  return new Promise<string>((resolve, reject) => {
    const image = new Image();

    image.src = "data:image/svg+xml;base64," + base64SVG;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = canvasSize;
      canvas.height = canvasSize;

      context.drawImage(image, 0, 0);

      const pngDataUrl = canvas.toDataURL("image/png");
      resolve(pngDataUrl);
    };

    image.onerror = (error) => {
      reject(error);
    };
  });
};

export const fileToBase64 = async (file: File) => {
  const fileReader = new FileReader();

  const promise = new Promise<string>((resolve) => {
    fileReader.onload = () => {
      resolve(fileReader.result as string);
    };
  });

  fileReader.readAsDataURL(file);

  return await promise;
};

export const InvalidVideoFormatToast = (t: TFunction) => {
  return (
    <div className="invalid-provider-toast">
      {t("incorrect_video_format")}: <span>.mp4, .h264, .mov, .mkv, .hevc, .ogg </span>
      {t("validation_and")}
      <span> .webm </span>
    </div>
  );
};

export const InvalidAudioFormatToast = (t: TFunction) => {
  return (
    <div className="invalid-provider-toast">
      {t("incorrect_audio_format")}: <span>.mp3, .ogg </span> {t("validation_and")}
      <span> .wav .</span>
    </div>
  );
};

export const InvalidProviderToast = (t: TFunction) => {
  return (
    <div className="invalid-provider-toast">
      {t("incorrect_link")}:
      <span>YouTube, Facebook, SoundCloud, Vimeo, Wistia, Mixcloud, DailyMotion </span>
      {t("validation_and")}
      <span> Twitch.</span>
    </div>
  );
};

export const InvalidYTProviderToast = (t: TFunction) => (
  <div className="invalid-provider-toast">
    {t("incorrect_link")}: <span>YouTube</span>
  </div>
);
